import React, { useMemo, useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import styles from '../../styles/PieChart.module.css';

/** SVG paths often do not resolve `var(--token)` like HTML does; legend/tooltip then disagree with slices. */
function resolveColorTokens(tokens, root) {
  const cs = getComputedStyle(root);
  return tokens.map((raw) => {
    const c = String(raw ?? '').trim();
    const m = /^var\(\s*(--[\w-]+)\s*(?:,[^)]+)?\)$/.exec(c);
    if (!m) return c;
    const resolved = cs.getPropertyValue(m[1]).trim();
    return resolved || c;
  });
}


export const DEFAULT_PIE_SERIES_COLORS = [
  'var(--button-primary)',
  'var(--button-secondary)',
  'var(--button-danger)',
  'var(--button-warning)',
];

function getPieLayout(showLegend, legendLayout) {
  const legendOnRight = legendLayout === 'right';
  const pieCenterY = !showLegend ? '48%' : legendOnRight ? '50%' : '45%';
  const pieCenterX = !showLegend ? '50%' : legendOnRight ? '40%' : '50%';
  const centerFont = !showLegend ? 30 : legendOnRight ? 24 : 28;
  const captionFont = Math.max(11, Math.round(centerFont * 0.4));
  return { legendOnRight, pieCenterX, pieCenterY, centerFont, captionFont };
}

const PieChart = React.memo(
  ({
    data = [],
    colors,
    colorBySliceName,
    width: propWidth,
    height: propHeight = 380,
    ariaLabel = 'pie chart',
    description,
    showNoDataMessage = true,
    onClick,
    className,
    isLoading = false,
    enableAnimation = process.env.NODE_ENV === 'production',
    enableHover = true,
    seriesName = 'Access From',
    tooltipFormatter,
    showLegend = true,
    /** `right`: vertical legend beside the pie (narrow columns). `bottom`: default horizontal legend. */
    legendLayout = 'bottom',
    centerColor = 'var(--text-neutral-primary)',
    centerLabel = '',
  }) => {
    const chartRef = useRef(null);
    const height = propHeight ?? 380;

    const defaultColors = useMemo(() => {
      if (colors && colors.length > 0) return colors;
      return DEFAULT_PIE_SERIES_COLORS;
    }, [colors]);

    const [themeRev, setThemeRev] = useState(0);
    useLayoutEffect(() => {
      const el = document.documentElement;
      const obs = new MutationObserver(() => setThemeRev((n) => n + 1));
      obs.observe(el, { attributes: true, attributeFilter: ['data-theme', 'class'] });
      return () => obs.disconnect();
    }, []);

    const resolvedColors = useMemo(() => {
      void themeRev;
      if (typeof window === 'undefined') return defaultColors;
      // Re-resolve CSS vars when theme attributes change (themeRev is the invalidation signal).
      void themeRev;
      return resolveColorTokens(defaultColors, document.documentElement);
    }, [defaultColors, themeRev]);

    /** Bind colors per slice so hover/tooltip/emphasis stay stable (avoids palette + CSS var flicker). */
    const seriesData = useMemo(() => {
      const root = typeof window !== 'undefined' ? document.documentElement : null;
      return (data || []).map((item, i) => {
        const existing = item || {};
        const named =
          colorBySliceName && item?.name != null
            ? colorBySliceName[String(item.name)]
            : undefined;
        const raw = named ?? existing?.itemStyle?.color ?? resolvedColors[i % resolvedColors.length];
        const sliceColor =
          root && String(raw).trim().startsWith('var(') ? resolveColorTokens([raw], root)[0] : raw;
        const isVeryLightFill =
          /^#fff(?:fff)?$/i.test(String(sliceColor).trim()) ||
          /^rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)$/i.test(String(sliceColor).trim());
        return {
          ...existing,
          itemStyle: {
            ...(existing.itemStyle || {}),
            color: sliceColor,
            ...(isVeryLightFill ? { borderColor: 'var(--border-neutral-secondary)' } : {}),
          },
        };
      });
    }, [data, resolvedColors, colorBySliceName]);

    const totalValue = useMemo(() => {
      if (!data || data.length === 0) return 0;
      return data.reduce((sum, item) => sum + (item?.value ? Number(item.value) : 0), 0);
    }, [data]);

    const resolvedTooltipFormatter = useMemo(() => {
      if (typeof tooltipFormatter === 'function') return tooltipFormatter;
      if (typeof tooltipFormatter === 'string' && tooltipFormatter.trim().length > 0) {
        return tooltipFormatter;
      }
      return (params) => {
        const value = params?.value || 0;
        const numValue = Number(value);
        const formattedValue = numValue.toLocaleString();
        const productName = params?.name ?? '';
        const pct =
          typeof params?.percent === 'number'
            ? params.percent.toFixed(1)
            : totalValue > 0
              ? ((numValue / totalValue) * 100).toFixed(1)
              : '0.0';
        const color =
          params?.color ??
          params?.data?.itemStyle?.color ??
          params?.data?.color ??
          'currentColor';
        const marker =
          params?.marker ??
          `<span style="display:inline-block;margin-right:6px;width:10px;height:10px;border-radius:9999px;background-color:${color};"></span>`;
        return `<span style="display:inline-flex;align-items:center;gap:6px;">${marker}<span>${productName}: ${formattedValue} (${pct}%)</span></span>`;
      };
    }, [tooltipFormatter, totalValue]);

    const formattedTotalValue = useMemo(() => totalValue.toLocaleString(), [totalValue]);

    const [hoverSlice, setHoverSlice] = useState(null);

    const handleSliceMouseOver = useCallback((params) => {
      if (params?.componentType !== 'series') return;
      const numericValue = typeof params.value === 'number' ? params.value : Number(params.value) || 0;
      setHoverSlice({
        value: numericValue,
        name: params.name ?? '',
      });
    }, []);

    const handleSliceMouseOut = useCallback(() => {
      setHoverSlice(null);
    }, []);

    const pieLayout = useMemo(
      () => getPieLayout(showLegend, legendLayout),
      [showLegend, legendLayout],
    );

    const centerDisplay = useMemo(() => {
      if (hoverSlice) {
        return {
          value: Number(hoverSlice.value).toLocaleString(),
          caption: hoverSlice.name,
        };
      }
      return {
        value: formattedTotalValue,
        caption: centerLabel,
      };
    }, [hoverSlice, formattedTotalValue, centerLabel]);

    const option = useMemo(() => {
      const legendData = seriesData.map((item) => item?.name);
      const { legendOnRight, pieCenterX, pieCenterY } = getPieLayout(showLegend, legendLayout);
      const pieRadius = legendOnRight
        ? ['38%', '62%']
        : showLegend
          ? ['40%', '71%']
          : ['42%', '74%'];

      const legendFormatter = function (name) {
        const dataArray = seriesData;
        if (!dataArray || dataArray.length === 0) return name;
        const dataItem = dataArray.find((item) => item && item.name === name);
        if (dataItem && dataItem.value !== undefined) {
          const total = dataArray.reduce((sum, item) => sum + (item?.value ? Number(item.value) : 0), 0);
          const value = Number(dataItem.value);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          return `${name}: ${value.toLocaleString()} (${percentage}%)`;
        }
        return name;
      };

      const legendBase = {
        show: showLegend,
        data: legendData,
        icon: 'circle',
        formatter: legendFormatter,
        textStyle: {
          color: 'var(--text-neutral-primary)',
          fontFamily: 'Manrope, sans-serif',
          fontSize: legendOnRight ? 12 : 14,
          fontWeight: 'normal',
        },
        itemWidth: 10,
        itemHeight: 10,
        type: 'plain',
        selectedMode: false,
      };

      const legendOption = legendOnRight
        ? {
            ...legendBase,
            orient: 'vertical',
            right: '2%',
            top: 'middle',
            itemGap: 10,
            align: 'left',
          }
        : {
            ...legendBase,
            orient: 'horizontal',
            bottom: '2%',
            left: 'center',
            width: '96%',
            itemGap: 24,
          };

      return {
        tooltip: enableHover
          ? {
              trigger: 'item',
              showDelay: 0,
              transitionDuration: 0,
              extraCssText:
                'pointer-events:none;box-shadow:var(--shadow-md);border-radius:var(--radius-lg);',
              backgroundColor: 'var(--background-neutral-primary)',
              borderWidth: 0,
              borderRadius: 0,
              padding: [12, 16],
              textStyle: {
                fontFamily: 'Manrope, sans-serif',
                fontSize: 14,
                color: 'var(--text-neutral-primary)',
                lineHeight: 20,
              },
              formatter: resolvedTooltipFormatter,
            }
          : { show: false },
        legend: legendOption,
        color: resolvedColors,
        series: [
          {
            name: seriesName,
            type: 'pie',
            radius: pieRadius,
            center: [pieCenterX, pieCenterY],
            startAngle: 80,
            sort: 'none',
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 0,
              borderColor: 'var(--background-neutral-primary)',
              borderWidth: 2,
            },
            label: { show: false },
            emphasis: {
              scale: false,
              focus: 'none',
              label: { show: false },
              itemStyle: {
                borderWidth: 2,
                borderColor: 'var(--background-neutral-primary)',
              },
            },
            labelLine: { show: false },
            data: seriesData,
          },
        ],
        animation: enableAnimation,
      };
    }, [
      seriesData,
      resolvedColors,
      seriesName,
      enableHover,
      enableAnimation,
      resolvedTooltipFormatter,
      formattedTotalValue,
      showLegend,
      legendLayout,
      centerColor,
      centerLabel,
    ]);

    const onEvents = useMemo(
      () => ({
        click: (params) => onClick?.(params?.name),
        mouseover: handleSliceMouseOver,
        mouseout: handleSliceMouseOut,
        globalout: handleSliceMouseOut,
      }),
      [handleSliceMouseOut, handleSliceMouseOver, onClick]
    );

    const style = useMemo(() => {
      return {
        width:
          propWidth !== undefined
            ? typeof propWidth === 'number'
              ? `${propWidth}px`
              : propWidth
            : undefined,
        height: typeof height === 'number' ? `${height}px` : height,
      };
    }, [propWidth, height]);

    return (
      <div className={['w-full', className].filter(Boolean).join(' ')} role="img" aria-label={ariaLabel}>
        {description ? <span className="sr-only">{description}</span> : null}

        {isLoading ? (
          <div className={styles.loading} style={{ height: typeof height === 'number' ? `${height}px` : height }} />
        ) : data.length === 0 && showNoDataMessage ? (
          <div className={styles.noDataWrap}>
            <p className={styles.noDataText}>No data available</p>
          </div>
        ) : (
          <div className={styles.chartWrap} style={style}>
            <ReactECharts
              ref={chartRef}
              option={option}
              className={styles.echartRoot}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'svg' }}
              onEvents={onEvents}
              notMerge={false}
              lazyUpdate
            />
            <p
              className={styles.pieCenter}
              style={{
                left: pieLayout.pieCenterX,
                top: pieLayout.pieCenterY,
                color: centerColor,
              }}
              aria-hidden="true"
            >
              <span
                className={styles.pieCenterValue}
                style={{ fontSize: pieLayout.centerFont }}
              >
                {centerDisplay.value}
              </span>
              {centerDisplay.caption ? (
                <span
                  className={styles.pieCenterCaption}
                  style={{ fontSize: pieLayout.captionFont }}
                >
                  {centerDisplay.caption}
                </span>
              ) : null}
            </p>
          </div>
        )}
      </div>
    );
  }
);

PieChart.displayName = 'PieChart';

export default PieChart;

